'use client';

import { Box, Stack, Typography } from '@mui/material';
import cx from 'clsx';

import Link from '@/components/Link';
import ListItem from '@/components/ListItem';
import { Automation, Engineering, Search, Strategy } from '@/components/SvgIcons/AiServices';
import { AiServiceGroup, AiServiceSlug } from '@/config/ai-services.config';
import colors from '@/styles/themes/colors';
import { useGlowEffect } from '@/utils/hooks/useGlowEffect';

import styles from './AIServiceCard.module.scss';

const accentColorMap: Record<AiServiceGroup['accentColor'], string> = {
  blue: colors.blue500,
  pink: colors.pink500,
  green: colors.green500,
  orange: colors.orange500,
};

const iconMap: Record<AiServiceSlug, typeof Strategy> = {
  'strategy-discovery': Strategy,
  'search-knowledge': Search,
  'product-development': Engineering,
  'powered-automation': Automation,
};

interface AIServiceCardProps {
  group: AiServiceGroup;
  title: string;
  items: string[];
  readMoreText: string;
  href: string;
}

const AIServiceCard = ({ group, title, items, readMoreText, href }: AIServiceCardProps) => {
  const accentColor = accentColorMap[group.accentColor];
  const { ref, onMouseMove, onMouseLeave, style } = useGlowEffect({ color: accentColor });

  return (
    <Stack ref={ref} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave} style={style} className={styles.card}>
      <Stack
        alignItems="center"
        justifyContent="center"
        className={cx(styles.iconWrapper, styles[group.accentColor])}
        aria-hidden="true"
      >
        {(() => {
          const Icon = iconMap[group.id];

          return <Icon fill={accentColor} size={42} />;
        })()}
      </Stack>
      <Typography variant="subtitle1" component="h3" className={styles.cardTitle} style={{ color: accentColor }}>
        {title}
      </Typography>
      <Box className={styles.listWrapper}>
        {items.map(item => (
          <ListItem key={item} text={item} variant={group.accentColor} />
        ))}
      </Box>
      <Link href={href} text={readMoreText} className={styles.readMore} />
    </Stack>
  );
};

export default AIServiceCard;
